import { useEffect, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Listbox,
} from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import NotificationModal from "./NotificationModal";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";

export default function CancelAssessmentModal({
  open,
  setOpen,
  onClick,
  sponsors,
  id,
  refetch,
}) {
  const [reason, setReason] = useState("");
  const allSponsorsOption = {
    sponsor_id: null,
    sponsor_name: "All",
    sponsor_contact_name: null,
    sponsor_contact_email: null,
    sponsor_contact_phone: null,
    is_primary_sponsor: false,
    sponsor_status: null,
  };
  const [selectedSponsor, setSelectedSponsor] = useState(allSponsorsOption);
  const [alertopen, setAlertOpen] = useState(false);
  const [message, setMessage] = useState("");

  const allSponsorsOptions = [
    allSponsorsOption,
    ...sponsors?.length > 0 ? sponsors.map((sponsor) => ({
      ...sponsor,
      disabled: sponsor.sponsor_status === "Canceled Assessment",
    })) : [],
  ];

  useEffect(() => {
    if (open) {
      setSelectedSponsor(allSponsorsOption);
      setReason("");
    }
  }, [open]);

  const handleSponsorChange = (newSponsor) => {
    if (!newSponsor.disabled) {
      setSelectedSponsor(newSponsor);
    }
  };

  const handleSubmit = () => {
    onClick(
      { vqaId: id, sponsor: selectedSponsor, reason },
      {
        onSuccess: () => {
          setAlertOpen(true);
          setMessage("Assessment Cancelled successfully");
          setTimeout(() => {
            setReason("");
            refetch();
            setOpen(false);
          }, 5000);
        },
      }
    );
  };

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-sm sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div>
              <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-yellow-100">
                <ExclamationTriangleIcon
                  aria-hidden="true"
                  className="size-6 text-yellow-600"
                />
              </div>
              <div className="mt-3 text-center sm:mt-5">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold text-gray-900"
                >
                  Canceled Assessment
                </DialogTitle>
                <div className="mt-4 text-left">
                  {/* Sponsor Selection */}
                  <label
                    htmlFor="sponsor-select"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Select Sponsor <span className="text-red-500">*</span>
                  </label>
                  <Listbox
                    value={selectedSponsor}
                    onChange={handleSponsorChange}
                  >
                    <div className="relative mt-1">
                      <Listbox.Button
                        id="sponsor-select"
                        className="relative w-full rounded-md border-gray-300 p-2 text-sm text-gray-900 text-left border focus:border-indigo-500 focus:ring-indigo-500"
                      >
                        <span className="block truncate">
                          {selectedSponsor.sponsor_name}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon
                            className="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                          />
                        </span>
                      </Listbox.Button>
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {allSponsorsOptions.map((sponsor, index) => (
                          <Listbox.Option
                            key={sponsor.sponsor_id || "all"}
                            value={sponsor}
                            disabled={sponsor.disabled}
                            className={({ active, disabled }) =>
                              `relative cursor-pointer select-none py-2 px-3 
                          ${
                            active && !disabled
                              ? "bg-indigo-100 text-indigo-900"
                              : "text-gray-900"
                          }
                          ${
                            disabled
                              ? "opacity-50 cursor-not-allowed bg-gray-100"
                              : ""
                          }
                          ${index === 0 ? "border-b border-gray-200" : ""}`
                            }
                          >
                            <div className="flex items-center justify-between">
                              <span
                                className={`block truncate ${
                                  sponsor.disabled ? "text-gray-500" : ""
                                }`}
                              >
                                {sponsor.sponsor_name}
                              </span>
                              {sponsor.disabled && (
                                <span className="text-xs text-gray-500 ml-2">
                                  (Cancelled)
                                </span>
                              )}
                            </div>
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </div>
                  </Listbox>

                  {/* Reason Input */}
                  <div className="mt-4">
                    <label
                      htmlFor="cancel-reason"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Reason for Cancellation{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="cancel-reason"
                      type="text"
                      required
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Enter reason for cancellation..."
                      className="w-full rounded-md border-gray-300 p-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-6">
              <button
                disabled={reason.length <= 0}
                type="button"
                onClick={handleSubmit}
                className={`inline-flex w-full justify-center rounded-md ${
                  reason.length <= 0 ? "bg-gray-600" : "bg-indigo-600"
                } px-3 py-2 text-sm font-semibold text-white shadow-sm ${
                  reason.length <= 0
                    ? "hover:bg-gray-600"
                    : "hover:bg-indigo-600"
                } focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600`}
              >
                Submit
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>

      <NotificationModal
        open={alertopen}
        setOpen={setAlertOpen}
        desc={message}
        title={"Success"}
        error={false}
      />
    </Dialog>
  );
}
